import { z } from 'zod';

// Versioned independently of INTEGRATION_EVENT_API_VERSION. Record payloads are pinned
// to the /v1 read shape; notification payloads are pinned to what renders a message.
export const RECORD_EVENT_API_VERSION = '2026-09-12';

export type IntegrationResourceOperation = 'list' | 'get' | 'create' | 'update' | 'delete';
export type IntegrationResourceScope = 'organization' | 'event';
export type IntegrationResourceIdentity = 'mapped' | 'none';

export interface IntegrationResourceDef {
	label: string;
	description: string;
	scope: IntegrationResourceScope;
	readSchema: z.ZodType;
	// Null until phase 3 opens writes. The field exists now so adding writes is a
	// registry edit rather than a shape change every consumer has to follow.
	writeSchema: z.ZodType | null;
	// Separate from writeSchema because creating and updating do not take the same
	// fields. A create has required fields an update must not demand, and an
	// update can change fields a create sets from context.
	createSchema: z.ZodType | null;
	operations: readonly IntegrationResourceOperation[];
	// Per-operation entitlement override. Absent means the default: reads need
	// the read capability, writes the write one. Present is how a resource whose
	// create can reach money asks for a stronger flag than its update.
	capabilities?: Partial<Record<IntegrationResourceOperation, 'read' | 'write'>>;
	identity: IntegrationResourceIdentity;
	emits: readonly string[];
	// Which of those also raise a notification, and as what. The notification
	// payload is this resource's readSchema, so the registry rather than a call
	// site decides: one declaration keeps the two planes' catalogues from
	// drifting, and adding a notifiable event stays a registry edit.
	notifies?: Readonly<Record<string, string>>;
	example: unknown;
}

const money = z.object({ amountCents: z.number().int(), currency: z.string().length(3) });

// Event configuration writes. Deliberately safer than the attendee surface: these
// touch no orders, no payments and no reservation.
//
// isPublished appears in neither schema. Publishing is a state transition with
// externally visible side effects and is hard to undo, so it is its own
// operation rather than a field somebody can flip while editing a venue name.
const eventCreate = z
	.object({
		name: z.string().min(1),
		description: z.string().default(''),
		ownerEmail: z.string().email(),
		eventTypeId: z.string().min(1),
		startDate: z.string(),
		endDate: z.string().nullish(),
		timezone: z.string().min(1),
		isPrivate: z.boolean().optional(),
		venue: z
			.object({
				name: z.string().min(1),
				address: z.string().min(1),
				placeId: z.string().optional()
			})
			.optional()
	})
	.strict();

const eventWrite = z
	.object({
		name: z.string().min(1),
		description: z.string(),
		startDate: z.string(),
		endDate: z.string().nullable(),
		timezone: z.string().min(1),
		isPrivate: z.boolean()
	})
	.partial()
	.strict();

const ticketCreate = z
	.object({
		eventId: z.string().min(1),
		name: z.string().min(1),
		description: z.string().nullish(),
		// Cents, like every other amount in this registry. A float here is how a
		// $10.00 ticket becomes $10.000000000000002.
		priceCents: z.number().int().min(0),
		quantityTotal: z.number().int().min(0).nullish(),
		salesStartAt: z.string().nullish(),
		salesEndAt: z.string().nullish()
	})
	.strict();

const attendeeRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	// Nullable because the column is. Requiring a string here threw inside the
	// caller's transaction and rolled back the attendee edit.
	email: z.string().nullable(),
	ticketId: z.string().nullable(),
	ticketName: z.string().nullable(),
	registrationTypeId: z.string().nullable(),
	registrationTypeName: z.string().nullable(),
	customData: z.record(z.string(), z.unknown()),
	// Event-level check-in only. Day and session points are a different
	// granularity and belong to the session resource; collapsing them here would
	// make "checked in" mean something different per event.
	checkedIn: z.boolean(),
	checkedInAt: z.string().nullable(),
	createdAt: z.string().nullable(),
	updatedAt: z.string().nullable()
});

const contactRead = z.object({
	id: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	email: z.string(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	createdAt: z.string().nullable(),
	// Standing as a membership sync last wrote it; null for a contact no sync owns.
	// Optional so payloads published before contacts carried standing still parse.
	membershipStatus: z.string().nullable().optional(),
	membershipLevel: z.string().nullable().optional(),
	joinedAt: z.string().nullable().optional(),
	renewsAt: z.string().nullable().optional(),
	externalMemberId: z.string().nullable().optional(),
	// Mapped custom fields by the name the organiser gave them. A choice arrives
	// as { id, label }.
	attributes: z.record(z.string(), z.unknown()).optional()
});

const contactListRead = z.object({
	id: z.string(),
	// Nullable, like tickets: an invite list belongs to the event it invites
	// people to, while a plain audience sits at the organization. Reported
	// because a list can be filtered BY event, and a record that can be narrowed
	// by a field it never states leaves the caller unable to check the narrowing
	// did what they asked.
	eventId: z.string().nullable(),
	eventName: z.string().nullable(),
	organizationId: z.string(),
	organizationName: z.string(),
	name: z.string(),
	// Where the list's membership comes from. An 'attendee' list is derived from
	// people registered for the event, so a bare contact cannot be put on one -
	// it would have to become an attendee first, which is a different and much
	// larger act than adding somebody to a list. Reported so a caller choosing a
	// list can tell which ones will accept a contact.
	source: z.enum(['attendee', 'invite']),
	memberCount: z.number().int(),
	createdAt: z.string().nullable(),
	// Present only on contact_list.member_added / member_removed: who joined or
	// left. A plain read of the list has no single member to name, so it is absent.
	member: z
		.object({
			contactId: z.string(),
			email: z.string(),
			firstName: z.string().nullable(),
			lastName: z.string().nullable()
		})
		.optional()
});

// Deliberately NOT a single `total`. orders.totalAmount is gross-at-creation and
// is never updated when items change, so one ambiguous number would show a CRM
// the wrong revenue. Each figure below names its own source instead:
//   totalAmountCents   - orders.totalAmount, the order's face value at creation
//   amountPaidCents    - settled cash in  (summarizeSettledPayments.cashPaid)
//   amountRefundedCents- settled cash out (summarizeSettledPayments.cashRefunded)
//   netPaidCents       - paid minus refunded
// Settled means PENDING and FAILED payment rows are excluded; summing those is
// what put $245.42 on a $0 order in the SUN-591 QA pass.
const orderRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	buyerEmail: z.string().nullable(),
	// Orders carry three state columns, not one status. Sending only paymentState
	// would hide a cancelled order that happens to have been paid.
	paymentState: z.string(),
	lifecycleState: z.string(),
	commitmentState: z.string(),
	currency: z.string().length(3),
	totalAmountCents: z.number().int(),
	amountPaidCents: z.number().int(),
	amountRefundedCents: z.number().int(),
	netPaidCents: z.number().int(),
	createdAt: z.string().nullable()
});

const registrationRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	attendeeIds: z.array(z.string()),
	// Denormalised from the attendee so a notification can say who registered
	// without a second load. Nullable because a status row can exist before its
	// attendee does.
	attendeeName: z.string().nullable(),
	attendeeEmail: z.string().nullable(),
	ticketName: z.string().nullable(),
	// From the registration's first order: tickets it bought (a group buyer's several), and what all its orders
	// paid so far. Nullish because records raised before these existed carry neither.
	ticketQuantity: z.number().int().nullish(),
	amountPaid: money.nullish(),
	completedAt: z.string().nullable(),
	createdAt: z.string().nullable()
});

// No `status`: events carries isPublished / isPrivate / isInternal booleans and
// no status column. venueName/venueAddress come from the venues table via
// venueId, and publicUrl is composed from the slug - all three need joins in the
// loader, which is why they are nullable rather than required.
const eventRead = z.object({
	id: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	name: z.string(),
	isPublished: z.boolean(),
	isPrivate: z.boolean(),
	startDate: z.string(),
	endDate: z.string().nullable(),
	timezone: z.string().nullable(),
	venueName: z.string().nullable(),
	venueAddress: z.string().nullable(),
	publicUrl: z.string().nullable(),
	createdAt: z.string().nullable()
});

const ticketRead = z.object({
	id: z.string(),
	// Nullable: tickets.event_id is, because an organization-level ticket type
	// exists before it is attached to an event.
	eventId: z.string().nullable(),
	eventName: z.string().nullable(),
	organizationId: z.string(),
	organizationName: z.string(),
	name: z.string(),
	price: money.nullable(),
	quantityTotal: z.number().int().nullable(),
	// Derived, not a column. The loader must reuse the existing availability
	// computation rather than defining "sold" a second time.
	quantitySold: z.number().int(),
	salesStartAt: z.string().nullable(),
	salesEndAt: z.string().nullable(),
	createdAt: z.string().nullable()
});

const sessionRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	title: z.string(),
	startAt: z.string(),
	endAt: z.string(),
	// locationId joins to a location; trackName comes from the session's tag.
	location: z.string().nullable(),
	trackName: z.string().nullable(),
	// Via the sessionSpeakers join table.
	speakerIds: z.array(z.string()),
	createdAt: z.string().nullable()
});

const speakerRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	// Composed from firstName + lastName, both nullable, so this can be absent.
	name: z.string().nullable(),
	title: z.string().nullable(),
	company: z.string().nullable(),
	bio: z.string().nullable(),
	// From photoKey, which is a storage key rather than a URL.
	photoUrl: z.string().nullable(),
	createdAt: z.string().nullable()
});

const sponsorRead = z.object({
	id: z.string(),
	eventId: z.string(),
	eventName: z.string(),
	organizationId: z.string(),
	organizationName: z.string(),
	name: z.string(),
	// Joins to sponsorTiers via sponsorTierId.
	tier: z.string().nullable(),
	websiteUrl: z.string().nullable(),
	// From logoKey, a storage key rather than a URL.
	logoUrl: z.string().nullable(),
	createdAt: z.string().nullable()
});

// Attendee writes are restricted to the strictly non-financial subset. ticketId,
// registrationTypeId, addons and sessionPackages all reach
// generateRegistrationOrder, so an integration carrying them could create orders
// and charge money. They are absent rather than validated-against, so adding one
// back is a deliberate act.
const attendeeWrite = z
	.object({
		firstName: z.string().min(1).nullable(),
		lastName: z.string().min(1).nullable(),
		email: z.string().email(),
		customData: z.record(z.string(), z.unknown())
	})
	.partial()
	.strict();

/**
 * The event and the list a contact action targets.
 *
 * A contact itself belongs to the organization, not to an event - so these are
 * inputs to the act of adding somebody, not fields of the record, and the
 * resource stays organization-scoped. The event is required because it is what
 * narrows the list choice: an organization's lists are not a useful thing to
 * scroll, and picking one from the wrong event is how an integration quietly
 * invites people to something else.
 */
const contactListTarget = {
	eventId: z.string(),
	contactListId: z.string()
};

const contactWrite = z
	.object({
		email: z.string().email(),
		firstName: z.string().nullable(),
		lastName: z.string().nullable()
	})
	.partial()
	// Extended after .partial() so these two stay required on an update as well:
	// an update that quietly skipped the list would look like it had added
	// somebody to it.
	.extend(contactListTarget)
	.strict();

/**
 * Email is the only required field, and it is the identity.
 *
 * A contact is unique per organization by email in the database, so a create
 * that names an email already present is the same contact, not a second one.
 * That makes this safe to call from a sync that cannot remember what it sent
 * last time, which is most of them.
 */
/**
 * What it takes to put somebody in an event.
 *
 * The ticket is required because an attendee is never just a row: creating one
 * generates a registration order at that ticket's price, and there is no
 * sensible default for which ticket somebody holds. Email is the identity, as
 * everywhere else here.
 */
const attendeeCreate = z
	.object({
		eventId: z.string(),
		ticketId: z.string(),
		email: z.string().email(),
		firstName: z.string().min(1),
		lastName: z.string().min(1),
		registrationTypeId: z.string().nullable().optional()
	})
	.strict();

const contactCreate = z
	.object({
		email: z.string().email(),
		firstName: z.string().nullable().optional(),
		lastName: z.string().nullable().optional(),
		...contactListTarget
	})
	.strict();

export const INTEGRATION_RESOURCES = {
	attendee: {
		label: 'Attendee',
		description: 'A person registered for an event',
		scope: 'event',
		readSchema: attendeeRead,
		writeSchema: attendeeWrite,
		createSchema: attendeeCreate,
		operations: ['list', 'get', 'create', 'update'],
		identity: 'mapped',
		emits: ['attendee.created', 'attendee.updated', 'attendee.cancelled', 'attendee.checked_in'],
		example: {
			id: '00000000-0000-0000-0000-0000000000a1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			firstName: 'Jane',
			lastName: 'Doe',
			email: 'jane@example.com',
			ticketId: '00000000-0000-0000-0000-0000000000t1',
			ticketName: 'General Admission',
			registrationTypeId: null,
			registrationTypeName: null,
			customData: { dietary: 'none' },
			checkedIn: true,
			checkedInAt: '2027-03-01T09:14:00.000Z',
			createdAt: '2026-09-12T00:00:00.000Z',
			updatedAt: null
		}
	},
	contact: {
		label: 'Contact',
		description: 'A person known to an organization, independent of any event',
		scope: 'organization',
		readSchema: contactRead,
		writeSchema: contactWrite,
		createSchema: contactCreate,
		operations: ['list', 'get', 'create', 'update'],
		identity: 'mapped',
		emits: ['contact.created', 'contact.updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000c1',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			email: 'jane@example.com',
			firstName: 'Jane',
			lastName: 'Doe',
			createdAt: '2026-09-12 00:00:00',
			membershipStatus: 'Active',
			membershipLevel: 'Gold',
			joinedAt: '2019-04-02 00:00:00',
			renewsAt: '2027-04-02 00:00:00',
			externalMemberId: '12345',
			attributes: { chapter: { id: '2', label: 'Midwest' } }
		}
	},
	contactList: {
		label: 'Contact list',
		description: 'A named list of contacts, including invite lists',
		// event_id is nullable, so a list CAN sit at the organization. The case
		// worth integrating is the event-scoped one: an invite list belongs to the
		// event it invites people to, and a rule that ignored that would move one
		// event's invitees into another event's sync.
		scope: 'event',
		readSchema: contactListRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'mapped',
		emits: ['contact_list.member_added', 'contact_list.member_removed'],
		example: {
			id: '00000000-0000-0000-0000-0000000000l1',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			name: 'VIPs',
			source: 'invite',
			memberCount: 12,
			createdAt: '2026-09-12T00:00:00.000Z',
			member: {
				contactId: '00000000-0000-0000-0000-0000000000c1',
				email: 'jane@example.com',
				firstName: 'Jane',
				lastName: 'Doe'
			}
		}
	},
	order: {
		label: 'Order',
		description: 'A purchase against an event',
		scope: 'event',
		readSchema: orderRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'none',
		emits: ['order.completed', 'order.refunded'],
		example: {
			id: '00000000-0000-0000-0000-0000000000o1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			buyerEmail: 'jane@example.com',
			paymentState: 'PAID',
			lifecycleState: 'ACTIVE',
			commitmentState: 'CONFIRMED',
			currency: 'usd',
			totalAmountCents: 15000,
			amountPaidCents: 15000,
			amountRefundedCents: 0,
			netPaidCents: 15000,
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	registration: {
		label: 'Registration',
		description: 'A completed registration, which may cover several attendees',
		scope: 'event',
		readSchema: registrationRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'none',
		emits: ['registration.completed'],
		notifies: { 'registration.completed': 'registration.completed' },
		example: {
			id: '00000000-0000-0000-0000-0000000000r1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			attendeeIds: ['00000000-0000-0000-0000-0000000000a1'],
			attendeeName: 'Jane Doe',
			attendeeEmail: 'jane@example.com',
			ticketName: 'General Admission',
			ticketQuantity: 1,
			amountPaid: { amountCents: 15000, currency: 'usd' },
			completedAt: '2026-09-12T00:00:00.000Z',
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	event: {
		label: 'Event',
		description: 'An event an organization is running',
		scope: 'organization',
		readSchema: eventRead,
		writeSchema: eventWrite,
		createSchema: eventCreate,
		operations: ['list', 'get', 'create', 'update'],
		identity: 'mapped',
		// Not event.created — that name already means internal telemetry with a
		// different payload in the notification registry.
		//
		// No event.published: nothing in this codebase reads events.is_published,
		// so there is no publish transition to report. Advertising it would let a
		// customer build an automation that never once fires.
		emits: ['event.record_created', 'event.record_updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000e1',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			name: 'Connect 2027',
			isPublished: true,
			isPrivate: false,
			startDate: '2027-03-01T09:00:00.000Z',
			endDate: '2027-03-03T17:00:00.000Z',
			timezone: 'America/New_York',
			venueName: 'Convention Center',
			venueAddress: '1 Main St',
			publicUrl: 'https://example.sunfish.events/connect-2027',
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	ticket: {
		label: 'Ticket type',
		description: 'A purchasable ticket type on an event',
		scope: 'event',
		readSchema: ticketRead,
		writeSchema: null,
		createSchema: ticketCreate,
		operations: ['list', 'get', 'create'],
		identity: 'mapped',
		emits: ['ticket.created', 'ticket.updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000t1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			name: 'General Admission',
			price: { amountCents: 15000, currency: 'USD' },
			quantityTotal: 500,
			quantitySold: 120,
			salesStartAt: null,
			salesEndAt: null,
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	session: {
		label: 'Session',
		description: 'An agenda session on an event',
		scope: 'event',
		readSchema: sessionRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'mapped',
		emits: ['session.created', 'session.updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000s1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			title: 'Opening keynote',
			startAt: '2027-03-01T09:00:00.000Z',
			endAt: '2027-03-01T10:00:00.000Z',
			location: 'Main Hall',
			trackName: null,
			speakerIds: [],
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	speaker: {
		label: 'Speaker',
		description: 'A speaker on an event',
		scope: 'event',
		readSchema: speakerRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'mapped',
		emits: ['speaker.created', 'speaker.updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000p1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			name: 'Jane Doe',
			title: 'CTO',
			company: 'Example Inc',
			bio: null,
			photoUrl: null,
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	},
	sponsor: {
		label: 'Sponsor',
		description: 'A sponsor on an event',
		scope: 'event',
		readSchema: sponsorRead,
		writeSchema: null,
		createSchema: null,
		operations: ['list', 'get'],
		identity: 'mapped',
		emits: ['sponsor.created', 'sponsor.updated'],
		example: {
			id: '00000000-0000-0000-0000-0000000000z1',
			eventId: '00000000-0000-0000-0000-0000000000e1',
			eventName: 'Connect 2027',
			organizationId: 'org_example',
			organizationName: 'Example Org',
			name: 'Example Sponsor',
			tier: 'Gold',
			websiteUrl: 'https://example.com',
			logoUrl: null,
			createdAt: '2026-09-12T00:00:00.000Z'
		}
	}
} as const satisfies Record<string, IntegrationResourceDef>;

export type IntegrationResourceKey = keyof typeof INTEGRATION_RESOURCES;

const DEFAULT_CAPABILITY: Record<string, 'read' | 'write'> = {
	get: 'read',
	list: 'read',
	update: 'write',
	create: 'write'
};

// The fallback is 'write', not 'read': an unrecognised operation must fail closed.
export function capabilityForOperation(
	resource: string,
	operation: 'get' | 'list' | 'update' | 'create'
): 'read' | 'write' {
	const def = INTEGRATION_RESOURCES[resource as keyof typeof INTEGRATION_RESOURCES] as
		| IntegrationResourceDef
		| undefined;
	return def?.capabilities?.[operation] ?? DEFAULT_CAPABILITY[operation] ?? 'write';
}

export type RecordEventType =
	(typeof INTEGRATION_RESOURCES)[IntegrationResourceKey]['emits'][number];

type ResourceOf<T extends RecordEventType> = {
	[K in IntegrationResourceKey]: T extends (typeof INTEGRATION_RESOURCES)[K]['emits'][number]
		? K
		: never;
}[IntegrationResourceKey];

export type RecordEventPayload<T extends RecordEventType> = z.infer<
	(typeof INTEGRATION_RESOURCES)[ResourceOf<T>]['readSchema']
>;

const EVENT_TO_RESOURCE = new Map<string, IntegrationResourceKey>(
	Object.entries(INTEGRATION_RESOURCES).flatMap(([key, def]) =>
		def.emits.map((type) => [type, key as IntegrationResourceKey] as const)
	)
);

export function isRecordEventType(value: string): value is RecordEventType {
	return EVENT_TO_RESOURCE.has(value);
}

/**
 * Whether a subscription to this type has to name the event it watches.
 *
 * An event-scoped resource only exists inside one event, so a rule that does not
 * say which one watches every event the organization will ever run, including
 * the ones it has not created yet. That is almost never what somebody building
 * a Zap means, and it is not something they would notice until a second event
 * started firing into the first one's sync.
 *
 * Organization-scoped resources have no event to name. A contact belongs to the
 * organization, not to an event, so requiring one there would be asking for a
 * value that cannot exist.
 */
export function requiresEventScope(type: RecordEventType): boolean {
	return INTEGRATION_RESOURCES[resourceForRecordEvent(type)].scope === 'event';
}

export function resourceForRecordEvent(type: RecordEventType): IntegrationResourceKey {
	const key = EVENT_TO_RESOURCE.get(type);
	if (!key) throw new Error(`[records] no resource declares ${type}`);
	return key;
}

/**
 * The notification this record event also raises, if any.
 *
 * Returns a plain string rather than an IntegrationEventType because this module
 * must not import integration-events: that dependency points the other way, so
 * the notification registry can pin a mirrored type's payload to a readSchema
 * here. The caller narrows with isIntegrationEventType.
 */
export function notificationTypeForRecordEvent(type: RecordEventType): string | null {
	// Read through the interface, not the `as const` literal: a resource that
	// declares no mirror has no `notifies` key in its literal type at all, so the
	// union has nothing to narrow.
	const def: IntegrationResourceDef = INTEGRATION_RESOURCES[resourceForRecordEvent(type)];
	return def.notifies?.[type] ?? null;
}

/**
 * Which event type means "this record was updated", for a given resource.
 *
 * Derived from the registry rather than built by template. `${resource}.updated`
 * is right for most resources and wrong for event, whose update type is
 * `event.record_updated` because `event.updated` already means internal
 * telemetry with a different payload. A template would produce a type no
 * resource declares, and emitRecord swallows that inside safeEmitRecord - so the
 * failure would be no webhook at all, with nothing in the log to say why.
 */
export function updatedEventTypeFor(resource: IntegrationResourceKey): RecordEventType | null {
	const emits = INTEGRATION_RESOURCES[resource]?.emits ?? [];
	// Both suffixes: the event resource's type is `event.record_updated`, which
	// does not end in `.updated` at all.
	const match = emits.find(
		(type) => type.endsWith('.updated') || type.endsWith('.record_updated')
	);
	return (match as RecordEventType) ?? null;
}

export function recordEventTypes(): RecordEventType[] {
	return [...EVENT_TO_RESOURCE.keys()] as RecordEventType[];
}

// Which event types actually have an emission call site today. A loader is not
// enough: attendee has one, but only attendee.updated is emitted - subscribing to
// attendee.created would create a rule that looks enabled and never fires.
//
// Add a type here in the same commit that adds its emit, never before.
export const EMITTED_RECORD_EVENT_TYPES = [
	'attendee.created',
	'attendee.updated',
	'attendee.cancelled',
	'attendee.checked_in',
	'contact.created',
	'contact.updated',
	'contact_list.member_added',
	'contact_list.member_removed',
	'order.completed',
	'registration.completed',
	'event.record_created',
	'event.record_updated',
	'ticket.created',
	'ticket.updated',
	'session.created',
	'session.updated',
	'speaker.created',
	'speaker.updated',
	'sponsor.created',
	'sponsor.updated'
] as const satisfies readonly RecordEventType[];

export function isEmittedRecordEventType(type: string): boolean {
	return (EMITTED_RECORD_EVENT_TYPES as readonly string[]).includes(type);
}
